import { toLocaleCurrencyString } from '@/lib/toLocaleCurrencyString';
import type { Price } from '@/types';
import { useTranslation } from 'react-i18next';

type Props = {
  price?: Price;
  trialPeriodDays?: number | null;
  isCampaignApplied?: boolean;
  hasTrial?: boolean;
  showTrialDescription?: boolean;
};

const formatDiscountValue = (value: number) =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

const getDiscountLabel = (price: Price) => {
  if (price.discountValue == null || price.discountValue <= 0) return null;
  if (!price.discountType) return null;

  if (price.discountType === 'percent') {
    return `${formatDiscountValue(price.discountValue)}%`;
  }

  const value = formatDiscountValue(price.discountValue);
  return price.discountCurrency ? `${price.discountCurrency} ${value}` : value;
};

export const PromotionPrice = ({
  price,
  trialPeriodDays,
  isCampaignApplied,
  hasTrial,
  showTrialDescription,
}: Props) => {
  const { t } = useTranslation();

  if (!price) return null;

  const { displayAmountMonthly, displayAmountDiscountedMonthly, currency } =
    price;
  const noPaymentMethodRequiredOnTrial =
    Boolean(price.noPaymentMethodRequiredOnTrial) && (trialPeriodDays ?? 0) > 0;

  const discountLabel = getDiscountLabel(price);
  const hasDiscount = Boolean(discountLabel);
  const trialEligible = Boolean(hasTrial) && (trialPeriodDays ?? 0) > 0;
  const shouldShowTrialDescription =
    Boolean(showTrialDescription) && trialEligible;
  const shouldShowCampaign =
    isCampaignApplied ?? (hasDiscount || trialEligible);

  if (!shouldShowCampaign) return null;

  let campaignDescription: string | null = null;
  if (hasDiscount && shouldShowTrialDescription) {
    campaignDescription = t(
      '割引と無料トライアル{{days}}日が適用されています',
      {
        days: trialPeriodDays,
      }
    );
  } else if (shouldShowTrialDescription) {
    campaignDescription = t('無料トライアル{{days}}日が適用されています', {
      days: trialPeriodDays,
    });
  }

  if (!hasDiscount && !shouldShowTrialDescription) return null;

  return (
    <div className="mt-4 rounded-xl bg-gradient-to-r from-pink-50 to-pink-100 px-8 py-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-pink-800">
            {t('キャンペーン特典適用中')}
          </div>
          {campaignDescription && (
            <div className="mt-1 text-xs text-pink-700">
              {campaignDescription}
            </div>
          )}
          {noPaymentMethodRequiredOnTrial && (
            <div className="mt-1 text-xs text-pink-700">
              {t('トライアル中は支払い方法の入力が不要です')}
            </div>
          )}
          {hasDiscount && (
            <div className="mt-1 flex items-baseline">
              <span className="text-sm text-gray-500 line-through">
                {toLocaleCurrencyString(displayAmountMonthly, currency)}
              </span>
              <span className="ml-4 text-2xl font-bold text-pink-700">
                {toLocaleCurrencyString(
                  displayAmountDiscountedMonthly,
                  currency
                )}
                <span className="ml-1 text-base">/ {t('月')}</span>
              </span>
            </div>
          )}
        </div>
        {hasDiscount && (
          <div className="rounded-full bg-pink-200 px-3 py-1 text-sm font-medium text-pink-800">
            {discountLabel}
          </div>
        )}
      </div>
    </div>
  );
};
